import { useEffect, useState } from "react";
import {
  VideoDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import { VideosService } from "../../services/videos.service";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import { FaPlus } from "react-icons/fa";

const Video = () => {
  const [videos, setVideos] = useState<VideoDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<VideoDataType | null>(
    null
  );
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");
  useEffect(() => {
    setLoading(true);
    VideosService.getAllVideos()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: VideoDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setVideos(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await VideosService.getAllVideos();
    setVideos(
      updatedData?.data?.data?.map((item: VideoDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const {
    handleSubmit,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
    handleChange,
  } = useFormik({
    initialValues: {
      title: "",
      time: "",
      link: "",
      cover_image: null,
      translations: [] as {
        lang_code: string;
        title: string;
        time: string;
      }[],
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Video başlığı tələb olunur"),
      time: Yup.string().required("Videonun vaxtı tələb olunur"),
      link: Yup.string()
        .url("Düzgün bir link daxil edin")
        .required("Videonun linki tələb olunur"),
      cover_image: Yup.string().required("Videonun şəkili tələb olunur"),
    }),
    onSubmit: async (values) => {
      const azTranslation = {
        lang_code: "AZ",
        title: values.title,
        time: values.time,
      };

      const updatedTranslations = [
        azTranslation,
        ...values.translations.filter((t) => t.lang_code !== "AZ"),
      ];

      const finalValues = {
        ...values,
        translations: updatedTranslations,
      };
      if (isEditing && editingRecord) {
        const isEqual =
          editingRecord.title === values.title &&
          editingRecord.time === values.time &&
          editingRecord.cover_image === values.cover_image &&
          editingRecord.translations === values.translations;
        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          if (editingRecord.cover_image === values.cover_image) {
            const payload = {
              title: values.title,
              time: values.time,
              link: values.link,
              translations: updatedTranslations,
            };
            const response = await VideosService.updateVideo({
              ...payload,
              id: editingRecord.id,
            });
            if (response.status === 200) {
              message.success("Video uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Videonu yeniləmək alınmadı."
              );
            }
          } else {
            const response = await VideosService.updateVideo({
              ...finalValues,
              id: editingRecord.id,
            });
            if (response.status === 200) {
              message.success("Video uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Videonu yeniləmək alınmadı."
              );
            }
          }
        }
      } else {
        const response = await VideosService.addVideo(finalValues);
        if (response.status === 201) {
          message.success("Video uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(response.errorMessage || "Video əlavə etmək alınmadı.");
        }
      }
      setLoading(false);
    },
  });
  const handleTranslationChange = (value: string, field: string) => {
    if (langCode === "AZ") {
      if (field === "title") {
        setFieldValue("title", value);
      } else if (field === "time") {
        setFieldValue("time", value);
      }
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
        };
      } else {
        updatedTranslations.push({
          lang_code: langCode,
          title: field === "title" ? value : "",
          time: field === "time" ? value : "",
        });
      }

      setFieldValue("translations", updatedTranslations);
    }
  };
  const handleDeleteClick = async (record: VideoDataType) => {
    try {
      await VideosService.deleteVideo(record.id.toString());
      setVideos((prevData) => prevData.filter((item) => item.id !== record.id));
      message.success("Video uğurla silindi!");
    } catch (error) {
      message.error("Videonu silmək alınmadı.");
    }
  };
  const columns: TableProps<VideoDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Videonun başlığı",
      dataIndex: "title",
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: "Videonun vaxtı",
      dataIndex: "time",
      render: (_, record) => (
        <p>
          {record.time?.length > 12
            ? record.time.substring(0, 12) + "..."
            : record.time}
        </p>
      ),
    },
    {
      title: "Videonun linki",
      dataIndex: "link",
      render: (_, record) => (
        <a href={record.link} rel="noreferrer" target="_blank">
          {record.link?.length > 20
            ? record.link.substring(0, 20) + "..."
            : record.link}
        </a>
      ),
    },
    {
      title: "Şəkil",
      dataIndex: "cover_image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.cover_image);
            setShowImageModal(true);
          }}
        >
          {record.cover_image ? (
            <img
              style={{ width: "40px" }}
              src={record.cover_image}
              alt={`${record.title.split(" ", 1)}`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu videonu siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
    setFieldValue("cover_image", null);
    setLogoFileList([]);
  };
  const handleEditClick = (record: VideoDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("title", record.title);
    setFieldValue("time", record.time);
    setFieldValue("link", record.link);
    setFieldValue("cover_image", record.cover_image);
    setFieldValue("translations", record.translations);
    setLangCode("AZ");
    setLogoFileList(
      record.cover_image
        ? [
            {
              uid: "-1",
              name: `${record.title} cover_image`,
              status: "done",
              url: record.cover_image,
            },
          ]
        : []
    );
  };

  return (
    <div id="pages">
      <Helmet>
        <title>1412 | Video təlimlər</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Video təlimlər</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<VideoDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={videos}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={isEditing ? "Videonu redaktə edin" : "Yeni video əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="title">
              Videonun başlığı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="title"
                id="title"
                placeholder="Videonun başlığı"
                value={
                  langCode === "AZ"
                    ? values.title
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.title || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "title")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.title} touched={touched.title} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="time">
              Videonun vaxtı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="time"
                id="time"
                placeholder="Videonun vaxtı"
                value={
                  langCode === "AZ"
                    ? values.time
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.time || ""
                }
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "time")
                }
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.time} touched={touched.time} />
          </div>

          <div className="form_input">
            <label className="label" htmlFor="link">
              Videonun linki
            </label>
            <div className="input_wrapper">
              <input
                name="link"
                id="link"
                placeholder="Videonun linki"
                value={values.link}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.link} touched={touched.link} />
          </div>
          <div className="form_input">
            <SelectImage
              image_name="Şəkil seçin"
              logoFileList={logoFileList}
              setLogoFileList={setLogoFileList}
              field="cover_image"
              setFieldValue={setFieldValue}
            />
            <ErrorMessage
              error={errors.cover_image}
              touched={touched.cover_image}
            />
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default Video;
